import React, { useState, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  getTranslatedText,
  CurrencyText,
  Logo,
  Loader
} from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import { useTable } from 'react-table';
import { getGlobalEntityAttributes } from '@nile/reports/src/modules/component/helper';
import { getMeta } from '@nile/reports/src/modules/home/helper/hooks';
import { TextStyle } from '@superops/ui-design-system';
import { isArray, isEmpty, map, sum } from 'lodash';
import { SummaryTableWrapper } from './styles';
import {
  getNonAuthToken,
  NonAuthAccountClient,
  AccountClient
} from '@nile/graphql';

const SummaryTable = (props) => {
  const {
    intl,
    data,
    groups = [],
    metrics = [],
    customClass,
    entity,
    chartRawData = {}
  } = props;

  const [headers, setHeaders] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);

  const [meta, setMeta] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);

  const totalArr = metrics
    .map((el) => ({
      [el?.metricId]:
        (data &&
          isArray(data) &&
          data.reduce(
            (n, dt) => {
              const arrValuee =
                Number(n?.value) +
                Number(sum(map(dt[el.metricId], 'value').map(Number)));

              const monoValue =
                Number(n?.value) + Number(dt[el.metricId]?.value);

              return isArray(dt[el.metricId])
                ? {
                    value: Number.isInteger(arrValuee)
                      ? arrValuee
                      : arrValuee?.toFixed(2),
                    cellType: dt[el.metricId]?.[0]?.cellType
                  }
                : {
                    value: Number.isInteger(monoValue)
                      ? monoValue
                      : monoValue?.toFixed(2),
                    cellType: dt[el.metricId]?.cellType
                  };
            },
            { value: 0, cellType: null }
          )) ||
        0
    }))
    .reduce((r, c) => Object.assign(r, c), {});

  const renderCell = (dataa, isFooter = false) => {
    const { value: displayData, cellType } = dataa || {};

    const summaryValue = chartRawData?.summaryData?.find(
      (el) => el?.metricId === dataa?.column?.id
    );

    const value = !isFooter ? displayData : summaryValue?.value || ' ';

    const renderCellType = isFooter ? summaryValue?.metricUnitType : cellType;
    switch (renderCellType) {
      case 'CURRENCY':
        return <CurrencyText value={value} />;
      default:
        return (
          <div>
            {value === 'NaN' || !value ? (
              <TextStyle variation="subdued">
                <FormattedMessage id="app.noData.message" />
              </TextStyle>
            ) : (
              value
            )}
          </div>
        );
    }
  };

  const getHeaderValue = (attribute, processedMeta) => {
    if (!attribute) return '';
    const atr = processedMeta?.groupByAttributes.find(
      (el) => el.name === attribute
    );
    const headerLabel =
      !isEmpty(processedMeta?.groupByAttributes) &&
      atr &&
      atr?.attributePlaceHolder?.label;
    return atr?.isCustom
      ? headerLabel || attribute
      : getTranslatedText(intl, headerLabel || attribute);
  };

  const generateHeader = (processedMeta) => {
    const headers = [];
    groups.map((grp) =>
      headers.push({
        Header: getHeaderValue(grp.attribute, processedMeta),
        accessor: grp.hasOwnProperty('unit')
          ? `${grp.attribute}.${grp.unit}`
          : grp.attribute,
        Cell: (props) => (
          <div>{props.cell.row.original[props.cell.column.id]}</div>
        )
      })
    );

    const unitHeader = (metr) => {
      if (metr.hasOwnProperty('metricUnit')) {
        return `${metr.name || metr.label} ${' '} ${(metr.hasOwnProperty(
          'metricUnit'
        ) &&
          metr.metricUnit &&
          `(${metr.metricUnit})`) ||
          ''}`;
      }
      return metr.name || metr.label;
    };
    metrics.map((met, ind) =>
      headers.push({
        Header: unitHeader(met) || met.name || met.label,
        accessor: met.metricId,
        id: met.metricId,
        Cell: (prop) =>
          renderCell(prop?.cell?.row?.original?.[prop?.cell?.column?.id])
      })
    );
    if (groups?.length) {
      headers[0].enableRowSpan = true;
    }
    setHeaders(headers);
    setLoading(false);
  };

  const generateColumns = () => {
    if (groups?.length >= 2) {
      const newData = [];
      const metricData = metrics.map((met) => met.metricId);
      Array.isArray(data) &&
        data.map((dataObj) => {
          const attr = groups[1].hasOwnProperty('unit')
            ? `${groups[1]?.attribute}.${groups[1]?.unit}`
            : groups[1]?.attribute;
          dataObj?.[attr]?.map((pr, ind) => {
            const metricObj = {};
            metricData.map((met) => {
              metricObj[met] = dataObj[met]?.[ind];
            });

            newData.push({
              ...dataObj,
              [attr]: pr,
              ...metricObj
            });
          });
        });
      setColumns(newData?.length ? newData : data);
    } else {
      setColumns(data);
    }
  };

  useEffect(() => {
    if (!isEmpty(meta)) {
      const processedMeta =
        !isEmpty(meta) && getGlobalEntityAttributes(intl, meta, [entity]);
      if (!isEmpty(processedMeta)) generateHeader(processedMeta);
    } else {
      generateHeader();
    }
  }, [meta]);

  useEffect(() => {
    if (data) {
      generateColumns();
    }
  }, [data]);

  useEffect(() => {
    // client based on token or non auth external user
    const client = getNonAuthToken() ? NonAuthAccountClient : AccountClient;
    setMetaLoading(true);
    getMeta(client).then(({ data: entData }) => {
      setMeta(entData?.getEntitiesMeta);
      setMetaLoading(false);
    });
  }, []);

  if (loading || metaLoading) {
    return (
      <div className="flex full-height full-width">
        <Loader />
      </div>
    );
  }
  if (!data || headers?.length === 0) {
    return (
      <div className="flex actr jctr full-height full-width">
        <div>
          <Logo
            url={`${envConfig.imageRoot}svg/chartEmpty.svg`}
            height="150px"
            width="200px"
          />
          <div className="flex actr jctr mt10">
            <TextStyle variation="subdued">
              <FormattedMessage id="reports.chart.data.empty" />
            </TextStyle>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ActualTable
      columns={headers}
      data={columns}
      customClass={customClass}
      renderCell={renderCell}
      chartRawData={chartRawData}
      totalArr={totalArr}
    />
  );
};

const ActualTable = (props) => {
  const {
    columns,
    data,
    customClass,
    renderCell,
    chartRawData,
    totalArr
  } = props;
  const useInstance = (instance) => {
    const { allColumns } = instance;

    let rowSpanHeaders = [];

    allColumns.forEach((column, i) => {
      const { id, enableRowSpan } = column;

      if (enableRowSpan !== undefined) {
        rowSpanHeaders = [
          ...rowSpanHeaders,
          { id, topCellValue: null, topCellIndex: 0 }
        ];
      }
    });

    Object.assign(instance, { rowSpanHeaders });
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    rowSpanHeaders
  } = useTable({ columns, data }, (hooks) => {
    hooks.useInstance.push(useInstance);
  });

  return (
    <SummaryTableWrapper className={customClass || 'summaryTable full-width'}>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>
                  {column.render('Header') === 'entityAttribute.assetClass.name'
                    ? 'Asset class'
                    : column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);

            for (let j = 0; j < row.allCells?.length; j++) {
              const cell = row.allCells[j];
              const rowSpanHeader = rowSpanHeaders.find(
                (x) => x.id === cell.column.id
              );

              if (rowSpanHeader !== undefined) {
                if (
                  rowSpanHeader.topCellValue === null ||
                  rowSpanHeader.topCellValue !==
                    cell.row.original[cell.column.id]
                ) {
                  cell.isRowSpanned = false;
                  rowSpanHeader.topCellValue =
                    cell.row.original[cell.column.id];
                  rowSpanHeader.topCellIndex = i;
                  cell.rowSpan = 1;
                } else {
                  rows[rowSpanHeader.topCellIndex].allCells[j].rowSpan++;
                  cell.isRowSpanned = true;
                }
              }
            }
            return null;
          })}
          {rows.map((row) => {
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  if (cell.isRowSpanned) return null;
                  return (
                    <td rowSpan={cell.rowSpan} {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr className="bold">
            {rows?.[0]?.cells?.map((el, idx) => {
              if (idx === 0) {
                return (
                  <td>
                    <FormattedMessage id="common.label.overall" />
                  </td>
                );
              }
              return (
                <td>
                  {isEmpty(chartRawData?.summaryData) ? (
                    <>
                      {totalArr?.hasOwnProperty(el?.column?.id)
                        ? renderCell(totalArr?.[el?.column?.id])
                        : ' '}
                    </>
                  ) : (
                    renderCell(el, true)
                  )}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </SummaryTableWrapper>
  );
};

export default injectIntl(SummaryTable);
